import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { Communication } from '@medplum/fhirtypes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  alignThreadRootsWithLatestDirectMessage,
  countUnreadMessagesForParticipant,
  fetchThreadsForUser,
} from '../services/messagingFhir';
import { isMessagingParticipantProfile, resolveMessagingCurrentUserRef } from '../services/messagingIdentity';
import { useMessagingRealtime } from './useMessagingRealtime';
import { createThreadRefreshCoalescer } from './useMessagingRealtime';

/** Custom event name dispatched when unread state changes (e.g. after marking a thread read). */
export const UNREAD_STATE_CHANGED_EVENT = 'unify:messaging:unread-state-changed';

/** Call from anywhere to signal the nav badge should refresh. */
export function notifyUnreadStateChanged(): void {
  window.dispatchEvent(new CustomEvent(UNREAD_STATE_CHANGED_EVENT));
}

const isDev = import.meta.env.DEV;

/**
 * Total unread messages across all conversations for nav badge.
 * Active globally (mounted in App.tsx).
 *
 * Subscribes to the shared MessagingRealtimeProvider (one broad WebSocket per tab).
 * On each message event, debounces a full unread recount so the badge stays correct
 * without N refetches during bursts.
 */
export function useUnreadThreadCount(): number {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [count, setCount] = useState(0);
  const loadingRef = useRef(false);

  const userRef = useMemo(() => {
    if (!profile?.id) return undefined;
    if (!isMessagingParticipantProfile(profile)) return undefined;
    return resolveMessagingCurrentUserRef(profile, false).reference;
  }, [profile]);

  const pendingReloadRef = useRef(false);

  const fullLoad = useCallback(async () => {
    if (!userRef) {
      setCount(0);
      return;
    }
    if (loadingRef.current) {
      pendingReloadRef.current = true;
      return;
    }
    loadingRef.current = true;
    if (isDev) {
      console.log('[Messaging][Badge] Full unread recount triggered');
    }
    try {
      medplum.invalidateSearches('Communication');
      const raw = await fetchThreadsForUser(medplum, userRef);
      const threads = await alignThreadRootsWithLatestDirectMessage(medplum, raw);
      const unreadMsgCount = await countUnreadMessagesForParticipant(medplum, threads, userRef);
      setCount(unreadMsgCount);
    } catch {
      setCount(0);
    } finally {
      loadingRef.current = false;
      if (pendingReloadRef.current) {
        pendingReloadRef.current = false;
        void fullLoad();
      }
    }
  }, [medplum, userRef]);

  const fullLoadRef = useRef(fullLoad);
  fullLoadRef.current = fullLoad;

  const coalescerRef = useRef<ReturnType<typeof createThreadRefreshCoalescer>>();
  if (!coalescerRef.current) {
    coalescerRef.current = createThreadRefreshCoalescer(() => {
      void fullLoadRef.current();
    }, 300);
  }

  const { onMessage, onReconnect } = useMessagingRealtime();

  useEffect(() => {
    const unsub = onMessage((comm: Communication) => {
      if (isDev) {
        console.log('[Messaging][Badge] Realtime message → debounced recount', comm.id);
      }
      coalescerRef.current?.enqueue(null);
    });
    return unsub;
  }, [onMessage]);

  useEffect(() => {
    const unsub = onReconnect(() => {
      if (isDev) {
        console.log('[Messaging][Badge] Reconnect → full reload');
      }
      void fullLoadRef.current();
    });
    return unsub;
  }, [onReconnect]);

  useEffect(() => {
    void fullLoad();
  }, [fullLoad]);

  useEffect(() => {
    const handler = (): void => {
      void fullLoad();
    };
    window.addEventListener(UNREAD_STATE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(UNREAD_STATE_CHANGED_EVENT, handler);
  }, [fullLoad]);

  useEffect(() => {
    const onVis = (): void => {
      if (document.visibilityState === 'visible') void fullLoad();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fullLoad]);

  useEffect(() => {
    return () => {
      coalescerRef.current?.cancel();
    };
  }, []);

  return count;
}
